import React from "react";
import { throttle } from "lodash";

import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";
import FloatingActionBar from "../FloatingActionBar/FloatingActionBar";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import PdfPage from "../PdfPage/PdfPage";

const SCROLL_THROTTLE_MS = 300;

interface Props extends Component {
  pages: string[];
  isLoading?: boolean;
  actionButtons?: React.ReactNode;
}

const isElementInView = (element: Element, top: number, bottom: number, height: number) => {
  const elementRect = element.getBoundingClientRect();
  if (
    (elementRect.bottom > bottom && elementRect.top > top && elementRect.top < bottom - height / 2) ||
    (elementRect.bottom > bottom && elementRect.top < top) ||
    (elementRect.top < top && elementRect.bottom > top + height / 2) ||
    (elementRect.top > top && elementRect.bottom < bottom)
  ) {
    return true;
  }

  return false;
};

const PdfViewer = ({ classNames, pages, actionButtons, isLoading = false }: Props) => {
  const [currentPage, setCurrentPage] = React.useState(1);

  // Create throttled scroll handler using useMemo
  const handleScroll = React.useMemo(() => {
    return throttle((pagesElement: HTMLDivElement, viewElement: HTMLElement) => {
      const { top, bottom, height } = viewElement.getBoundingClientRect();

      const elements = Array(pagesElement.children.length)
        .fill(0)
        .map((_, index) => pagesElement.children.item(index));

      const currentPageIndex = elements
        .map(el => el && isElementInView(el, top, bottom, height))
        .findIndex(visible => visible);

      if (currentPageIndex >= 0) {
        setCurrentPage(currentPageIndex + 1);
      }
    }, SCROLL_THROTTLE_MS);
  }, []);

  // Create stable scroll handler that gets DOM elements at call time
  const onScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const pagesElement = event.currentTarget;
      const viewElement = pagesElement.parentElement;
      if (viewElement) {
        handleScroll(pagesElement, viewElement);
      }
    },
    [handleScroll]
  );

  return (
    <div className={cn("PdfViewer", [], classNames)}>
      <div onScroll={onScroll} className="PdfViewer__pages">
        {isLoading && (
          <div className="PdfViewer__loading">
            <LoadingIndicator />
          </div>
        )}
        {!isLoading &&
          pages.map((page, index) => (
            <div key={index} className="PdfViewer__page">
              <PdfPage source={page} />
            </div>
          ))}
      </div>
      <div className="PdfViewer__info">
        <FloatingActionBar
          info={
            isLoading ? (
              <LoadingIndicator size="s" variant="standout" />
            ) : (
              <>
                {currentPage}/{pages.length}
              </>
            )
          }
          actions={actionButtons}
        />
      </div>
    </div>
  );
};

export default PdfViewer;
